import { Maybe, Result } from '@zaptic-external/ts-monads'
import {
    type FileExtension,
    isKnownFileExtension,
    isPhotoExtension,
    isVideoExtension,
} from '../../../shared/domain/files/fileExtensions.no-deps'
import type * as File from '../../../shared/domain/files/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import { compact, map, mapAsync, uniq } from '../../../shared/lib/functional/array.no-deps'
import { assertExhaustion, type Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import * as UrlPatterns from '../../../shared/lib/urlPatterns.no-deps'
import { getPathPrefix } from '../../../shared/webcomponents/helpers/href'
import type { IconName } from '../../../shared/webcomponents/helpers/icons'
import type { CommonDeps } from '../base/CommonDeps'
import { type ThumbnailUrls, fetchS3Urls } from '../file/withS3Url'
import type {
    AppFileGuideDetails,
    AppGuide,
    FileGuideDownloadUrl,
    FileGuideExecution,
    GuideExecution,
    ParentInfo,
    ReportGuideExecution,
    UrlGuideExecution,
    WorkflowGuideExecution,
} from './Model'
import type { KnownUnit } from '../../../shared/domain/units/types.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { sortAlphabetical } from '../../../shared/lib/string.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { promiseThen } from '../../../shared/lib/functional/promiseThen.no-deps'

export interface FileGuideDownloadUrls {
    [key: File.FileId]: FileGuideDownloadUrl
}

export async function fetchFileGuideDownloadUrls(
    deps: CommonDeps,
    fileIds: File.FileId[]
): Promise<FileGuideDownloadUrls> {
    const toDownloadUrls = (mapper: (fileId: File.FileId) => FileGuideDownloadUrl): FileGuideDownloadUrls => {
        const downloadUrls: FileGuideDownloadUrls = {}

        for (const fileId of fileIds) {
            downloadUrls[fileId] = mapper(fileId)
        }

        return downloadUrls
    }

    return fetchS3Urls(deps, fileIds, false)
        .then((s3Urls) => toDownloadUrls((fileId) => Result.ok(Maybe.fromOptional(s3Urls[fileId]))))
        .catch(() => toDownloadUrls(() => Result.err('API')))
}

export async function createReportGuideDetailsMap(
    fetchFn: (reportGuideIds: Guide.ReportGuideId[]) => Promise<Guide.ReportGuideDetails[]>,
    guides: Guide.Guide[]
): Promise<Map<Guide.Id, Guide.ReportGuideDetails>> {
    // Create a map containing all the report guide detail IDs.
    const reportGuideDetailIds = guides.reduce<Map<Guide.ReportGuideId, Guide.Id>>((acc, guide) => {
        if (guide.variant === 'report') {
            acc.set(guide.report, guide.id)
        }
        return acc
    }, new Map())

    // If we have at least one report guide we will have to fetch the details for them from the API.
    if (reportGuideDetailIds.size > 0) {
        const ids = uniq([...reportGuideDetailIds.keys()])
        const reportGuideDetails = await fetchFn(ids)

        return new Map(
            reportGuideDetails.map((details) => {
                const guideId = reportGuideDetailIds.get(details.id)

                return [guideId, details]
            })
        )
    }

    return new Map()
}

export async function createGuideExecution(
    guide: Guide.SyncGuide,
    fetchFileGuideDetails: (fileId: File.FileId) => Promise<AppFileGuideDetails>,
    fetchGuideParentInfo: () => Promise<ParentInfo[]>,
    fetchReportGuideDetails: (reportGuideId: Guide.ReportGuideId) => Promise<Guide.ReportGuideDetails>
): Promise<GuideExecution> {
    const variant = guide.variant

    switch (variant) {
        case 'file':
            return createFileGuideExecution(guide, fetchFileGuideDetails, fetchGuideParentInfo)
        case 'report':
            return createReportGuideExecution(guide, fetchReportGuideDetails, fetchGuideParentInfo)
        case 'url':
            return createUrlGuideExecution(guide, fetchGuideParentInfo)
        case 'workflow':
            return createWorkflowGuideExecution(guide, fetchGuideParentInfo)
        default:
            assertExhaustion(variant)
    }
}

export function getGuideTileSummaryIcon(guideVariant: Guide.Variant): IconName {
    return guideVariant === 'url' ? 'external-link' : undefined
}

interface GuideIconsResult {
    fileExtensionMap: Map<File.FileId, FileExtension>
    fileMetaMap: Map<File.FileId, File.FileMetaApp>
    iconUrlMap: Map<Guide.Id, string>
}

export async function fetchGuideIcons(
    deps: CommonDeps,
    guides: Guide.SyncGuide[],
    reportGuideDetailsMap: Map<Guide.Id, Guide.ReportGuideDetails>,
    // File metas proveider function.
    fetchFileMetasFn: (fileIds: File.FileId[]) => Promise<File.FileMetaApp[]>,
    // Callers can use this to tell this function if it should request the thumbnail or full size verions.
    shouldUseThumbnailFn = (_: File.FileMetaApp[]) => true,
    // Set this to false when you don't want to fetch the thumbnails and they are present in guides already.
    shouldFetchThumbnails: boolean = true
): Promise<GuideIconsResult> {
    const fileIdMap = await fetchIconFileIds(guides, reportGuideDetailsMap)
    const fileIds = uniq([...fileIdMap.values()])
    const fileMetas = await fetchFileMetasFn([...fileIds])

    const fileExtensionMap = new Map<File.FileId, FileExtension>()
    const fileMetaMap = new Map<File.FileId, File.FileMetaApp>()

    fileMetas.forEach((fileMeta) => {
        fileExtensionMap.set(fileMeta.fileId, fileMeta.extension)
        fileMetaMap.set(fileMeta.fileId, fileMeta)
    })

    let iconUrlMap = new Map<Guide.Id, string>()

    // Ask API to generate the thumbnail URLs if shouldFetchThumbnails is set to true.
    const useThumbnail = shouldUseThumbnailFn(fileMetas)
    const thumbnailUrls: ThumbnailUrls = shouldFetchThumbnails
        ? await fetchS3Urls(deps, fileIds, useThumbnail).catch(() => ({}))
        : getThumbnailUrlsFromGuides(guides)

    // Build the icon URLs map.
    iconUrlMap = new Map(
        guides.map((guide) => [
            guide.id,
            getGuideIconUrl(
                guide,
                (guideId) => fileIdMap.get(guideId),
                (fileId) => fileExtensionMap.get(fileId),
                (fileId) => Maybe.fromOptional(thumbnailUrls[fileId])
            ),
        ])
    )

    return { fileExtensionMap, fileMetaMap, iconUrlMap }
}

function getThumbnailUrlsFromGuides(guides: Guide.SyncGuide[]) {
    return guides.reduce((thumbnailUrls: ThumbnailUrls, guide: Guide.SyncGuide) => {
        if (guide.variant === 'file' || guide.variant === 'report') {
            thumbnailUrls = { ...thumbnailUrls, ...(guide.fileThumbnailUrl || {}) }
        }
        return thumbnailUrls
    }, {})
}

async function fetchIconFileIds(
    guides: Guide.Guide[],
    reportGuideDetailsMap: Map<Guide.Id, Guide.ReportGuideDetails>
): Promise<Map<Guide.Id, File.FileId>> {
    // All file guides' files are included in result.
    const fileIdsResult = guides.reduce<Map<Guide.Id, File.FileId>>((acc, guide) => {
        if (guide.variant === 'file') {
            acc.set(guide.id, guide.file)
        }
        return acc
    }, new Map())

    for (const [guideId, reportGuideDetails] of reportGuideDetailsMap.entries()) {
        if (reportGuideDetails.firstImage) {
            fileIdsResult.set(guideId, reportGuideDetails.firstImage)
        }
    }

    return fileIdsResult
}

/**
 * Convert parent information from app-core type to shared type.
 *
 * @param parentInfo app-core type
 * @returns shared type
 */
export function toSharedParentInfo(parentInfo: ParentInfo): Guide.ParentInfo {
    switch (parentInfo.type) {
        case 'procedure':
            return { procedure: parentInfo.id, type: 'procedure' }
        case 'unit':
            return { unit: parentInfo.id, type: 'unit' }
    }
}

function getGuideIconUrl(
    guide: Guide.Guide,
    getFileId: (guideId: Guide.Id) => File.FileId,
    getFileExtension: (fileId: File.FileId) => FileExtension,
    getThumbnailUrl: (fileId: File.FileId) => Maybe<string>
): string {
    const defaultIconName = getDefaultGuideIconNameForVariant(guide.variant)

    if (guide.variant === 'workflow') {
        return getSharedImagePath(defaultIconName)
    }

    if (guide.variant === 'url') {
        const imageName = `${getUrlGuideIconName(guide.url)}.svg`
        return getSharedImagePath(imageName)
    }

    const fileId = getFileId(guide.id)
    const fileExtension = getFileExtension(fileId)
    return getFileGuideIconUrl(fileId, fileExtension, getThumbnailUrl)
}

export function getFileGuideIconUrl(
    fileId: File.FileId,
    fileExtension: FileExtension,
    getThumbnailUrl: (fileId: File.FileId) => Maybe<string>
) {
    const defaultIconName = getDefaultGuideIconNameForVariant('file')

    if (!isKnownFileExtension(fileExtension)) {
        return getSharedImagePath(defaultIconName)
    }

    if (isPhotoExtension(fileExtension) || isVideoExtension(fileExtension)) {
        const maybeThumbnailUrl = getThumbnailUrl(fileId)

        return maybeThumbnailUrl.withDefault(getSharedImagePath(defaultIconName))
    }

    switch (fileExtension) {
        case '.pdf':
            return getSharedImagePath('file-pdf.svg')
        case '.doc':
            return getSharedImagePath('file-doc.svg')
        case '.docx':
            return getSharedImagePath('file-docx.svg')
        case '.xls':
            return getSharedImagePath('file-xls.svg')
        case '.xlsx':
            return getSharedImagePath('file-xlsx.svg')
        case '.ppt':
            return getSharedImagePath('file-ppt.svg')
        case '.pptx':
            return getSharedImagePath('file-pptx.svg')
        case '.csv':
            return getSharedImagePath('file-csv.svg')
        default:
            return getSharedImagePath(defaultIconName)
    }
}

function getDefaultGuideIconNameForVariant(variant: Guide.Variant): string {
    switch (variant) {
        case 'report':
            return 'report-square.svg'
        default:
            return 'question-mark-grey.svg'
    }
}

export function getSharedImagePath(imageName: string): string {
    const pathPrefix = getPathPrefix()

    return `${pathPrefix}shared/images/${imageName}`
}

async function createFileGuideExecution(
    guide: Guide.SyncGuide<'file'>,
    fetchFileGuideDetails: (fileId: File.FileId) => Promise<AppFileGuideDetails>,
    fetchGuideParentInfo: () => Promise<ParentInfo[]>
): Promise<FileGuideExecution> {
    const fileId = guide.file
    const { downloadUrl, fileExtension } = await fetchFileGuideDetails(fileId)
    const parentInfo = await fetchGuideParentInfo()

    return {
        customData: {
            downloadUrl,
            fileExtension,
        },
        guide,
        parentInfo,
        variant: 'file',
    }
}

export async function createReportGuideExecution(
    guide: Guide.SyncGuide<'report'>,
    fetchReportGuideDetails: (reportGuideId: Guide.ReportGuideId) => Promise<Guide.ReportGuideDetails>,
    fetchGuideParentInfo: () => Promise<ParentInfo[]>
): Promise<ReportGuideExecution> {
    const reportGuideId = guide.report
    const { data: customData } = await fetchReportGuideDetails(reportGuideId)
    const parentInfo = await fetchGuideParentInfo()

    return {
        customData,
        guide,
        parentInfo,
        variant: 'report',
    }
}

async function createUrlGuideExecution(
    guide: Guide.SyncGuide<'url'>,
    fetchGuideParentInfo: () => Promise<ParentInfo[]>
): Promise<UrlGuideExecution> {
    const parentInfo = await fetchGuideParentInfo()

    return {
        guide,
        parentInfo,
        variant: 'url',
    }
}

async function createWorkflowGuideExecution(
    guide: Guide.SyncGuide<'workflow'>,
    fetchGuideParentInfo: () => Promise<ParentInfo[]>
): Promise<WorkflowGuideExecution> {
    const parentInfo = await fetchGuideParentInfo()

    return {
        guide,
        parentInfo,
        variant: 'workflow',
    }
}

function getUrlGuideIconName(url: string): IconName {
    const defaultUrlIcon = 'link'

    // A map of regular expression for some doc url patterns and their icon name
    const urlPatternIconMap: Map<RegExp, IconName> = new Map<RegExp, IconName>([
        [UrlPatterns.googleDrive, 'google-drive'],
        [UrlPatterns.oneDrive, 'onedrive'],
        [UrlPatterns.microsoftTeams, 'microsoft-teams'],
        [UrlPatterns.googleDocs, 'google-docs'],
        [UrlPatterns.microsoftWordOnline, 'microsoft-word'],
        [UrlPatterns.dropbox, 'dropbox'],
        [UrlPatterns.notion, 'notion'],
        [UrlPatterns.microsoftExcel, 'microsoft-excel'],
        [UrlPatterns.microsoftPowerpoint, 'microsoft-powerpoint'],
        [UrlPatterns.googleSheet, 'google-sheets'],
        [UrlPatterns.microsoftSharepoint, 'microsoft-sharepoint'],
    ])

    for (const [urlPattern, icon] of urlPatternIconMap) {
        const regexpMatched = urlPattern.test(url)
        if (regexpMatched) return icon || defaultUrlIcon
    }

    return defaultUrlIcon
}

export async function fetchReportGuideDetails(
    deps: CommonDeps,
    reportGuideId: Guide.ReportGuideId
): Promise<Guide.ReportGuideDetails> {
    const { reportGuideDetails } = await deps.httpClient.getReportGuideDetailsBulk({ ids: [reportGuideId] })
    return reportGuideDetails[0]
}

export async function fetchGuideParentInfo(
    deps: CommonDeps,
    guide: Guide.SyncGuide,
    allUnits: KnownUnit[]
): Promise<Nullable<ParentInfo[]>> {
    const proceduresMap = await deps.httpClient
        .getProcedures(guide.procedures)
        .then((results) => new Map(results.procedures.map(({ id, name }) => [id, name])))

    const procedureParentInfo: ParentInfo[] = pipe(
        guide.procedures,
        map((procedureId) => {
            return {
                type: 'procedure',
                id: procedureId,
                name: proceduresMap.get(procedureId),
                procedure: {
                    id: procedureId,
                    name: proceduresMap.get(procedureId),
                    description: '',
                },
            }
        })
    )

    const unitParentInfo: ParentInfo[] = allUnits
        .filter((unit) => guide.units.includes(unit.id))
        .map((unit) => {
            // unit is a KnownUnit but we need an Identifiable<TSimpleUnit>
            const { lastMod, children, parents, kind, isDeleted, ...rest } = unit
            return { type: 'unit', id: unit.id, name: unit.name, unit: rest }
        })

    const parentInfo = sortAlphabetical([...procedureParentInfo, ...unitParentInfo], (item: ParentInfo) => item.name)

    return parentInfo.length > 0 ? parentInfo : null
}

export async function fetchFileGuideDetailsMap(
    deps: CommonDeps,
    fileIds: FileId[]
): Promise<Map<FileId, AppFileGuideDetails>> {
    const downloadUrls = await fetchFileGuideDownloadUrls(deps, fileIds)
    const fileExtensionMap = await fetchFileGuideExtensionMap(deps, fileIds)

    const detailsMap = new Map<FileId, AppFileGuideDetails>()

    for (const fileId of fileIds) {
        const downloadUrl = downloadUrls[fileId]

        if (downloadUrl) {
            const fileExtension = fileExtensionMap.get(fileId)

            detailsMap.set(fileId, {
                downloadUrl,
                fileExtension,
            })
        }
    }

    return detailsMap
}

export async function fetchFilesMeta(deps: CommonDeps, fileIds: FileId[]): Promise<File.FileMetaApp[]> {
    const { fileMeta } = await deps.httpClient.getFileMetaBulk({ fileIds })

    return fileMeta
}

export async function fetchFileGuideExtensionMap(
    deps: CommonDeps,
    fileIds: FileId[]
): Promise<Map<FileId, FileExtension>> {
    // Get file meta data for these file ids
    const fileMeta = await fetchFilesMeta(deps, fileIds)

    // Create a map
    const filesMetaDataMap = new Map<FileId, FileExtension>(
        fileMeta.map((fileMata) => [fileMata.fileId, fileMata.extension])
    )
    return filesMetaDataMap
}

export async function fetchAppGuides(
    deps: CommonDeps,
    syncGuides: Guide.SyncGuide[],
    withParentInfo: boolean,
    fetchGuideParentInfo?: (syncGuide: Guide.SyncGuide) => Promise<ParentInfo[]>
): Promise<AppGuide[]> {
    const reportGuideDetailsMap = await createReportGuideDetailsMap(
        (ids) =>
            deps.httpClient.getReportGuideDetailsBulk({ ids }).then(({ reportGuideDetails }) => reportGuideDetails),
        syncGuides
    )
    const { fileExtensionMap, iconUrlMap } = await fetchGuideIcons(deps, syncGuides, reportGuideDetailsMap, (fileIds) =>
        fetchFilesMeta(deps, fileIds)
    )

    return pipe(
        syncGuides,
        mapAsync(async (guide: Guide.SyncGuide) =>
            syncGuideToAppGuide(
                guide,
                iconUrlMap,
                fileExtensionMap,
                withParentInfo && fetchGuideParentInfo && (await fetchGuideParentInfo(guide))
            )
        ),
        promiseThen(compact)
    )
}

export function syncGuideToAppGuide(
    syncGuide: Guide.SyncGuide,
    iconUrlMap: Map<Guide.Id, string>,
    fileExtensionMap: Map<File.FileId, FileExtension>,
    parentInfo?: ParentInfo[]
): AppGuide {
    const iconUrl = iconUrlMap.get(syncGuide.id)

    if (syncGuide.variant === 'file') {
        const fileExtension = fileExtensionMap.get(syncGuide.file)

        return {
            ...syncGuide,
            iconUrl,
            isVideoFileGuide: isVideoExtension(fileExtension),
            extension: fileExtension,
            parentInfo,
        }
    }

    return { ...syncGuide, iconUrl, parentInfo }
}
